import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import {
  Box,
  Button,
  MenuItem,
  Menu,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import PrintIcon from "@mui/icons-material/Print";

// Components and hooks
import { createDispatchColumns } from "./DispatchDataGridColumns";
import { useDispatchData } from "../hooks/useDispatchData";
import PropertyContext from "../contexts/PropertyContext";
import AutoDispatchModal from "./AutoDispatchModal";
import ChecklistModal from "./ChecklistModal";

// Utils
import { validateDispatchItem, createNewDispatchItem } from "../utils/dispatchUtils";
import { downloadDispatchPDF } from "../utils/printDispatchPDF";

// Times entered in the grid's TimePicker default to *today's* date, and the
// auto-dispatch algorithm uses a placeholder date — both are wrong when
// dispatching for another day and corrupt sorting/duration. Re-anchor a time to
// the dispatch's calendar date (derived from the UTC date key so it matches the
// Firestore doc path) while keeping the chosen local hour/minute.
const dispatchDateTime = (selectedDate, hour, minute) => {
  const iso = new Date(selectedDate).toISOString().split("T")[0]; // YYYY-MM-DD
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  return dayjs()
    .year(y)
    .month(m - 1)
    .date(d)
    .hour(hour)
    .minute(minute)
    .second(0)
    .millisecond(0);
};

const DispatchNew = React.memo(() => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Default to today, anchored to 06:00 UTC to match the save/date-key logic
    // (formatDateForFirestore keys off the UTC calendar date).
    const d = dayjs().startOf("day").toDate();
    d.setUTCHours(6, 0, 0, 0);
    return d.getTime();
  });
  const [addedEmployeeIds, setAddedEmployeeIds] = useState([]);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    severity: "info",
  });
  const [autoDispatchModalOpen, setAutoDispatchModalOpen] = useState(false);
  const [checklistModalOpen, setChecklistModalOpen] = useState(false);
  const [selectedDispatchId, setSelectedDispatchId] = useState(null);
  const [selectedChecklists, setSelectedChecklists] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const { selectedProperty, selectedClient, rooms, items } = useContext(PropertyContext);

  const {
    loading,
    saving,
    employeeList,
    roomsList,
    selectedEmployeeDispatch,
    fetchEmployeeDispatches,
    addNewDispatchRow,
    updateDispatchItem,
    removeDispatchItem,
    replaceAllDispatchItems,
    saveDispatchItems,
  } = useDispatchData(selectedClient, selectedProperty);

  // Autosave bookkeeping
  const dirtyRef = useRef(false); // an edit happened since the last save
  const autosaveRef = useRef(null); // latest autosave fn, for unmount/hide flush
  const boardWrapRef = useRef(null); // grid wrapper, to detect focus leaving it

  // Fetch dispatches when employee or date changes
  React.useEffect(() => {
    if (!selectedEmployee || !selectedDate) return;

    // Seed the first row's start time with the employee's scheduled shift start
    // for this weekday (stored as an "HH:MM" string), anchored to the selected date.
    const dayName = new Date(selectedDate).toLocaleDateString("en-US", {
      weekday: "long",
      timeZone: "UTC",
    });
    const shiftStart = employeeList.find((e) => e.id === selectedEmployee)
      ?.schedule?.[dayName]?.startTime;

    let defaultStart = null;
    if (typeof shiftStart === "string" && shiftStart.includes(":")) {
      const [h, m] = shiftStart.split(":").map((n) => parseInt(n, 10));
      if (!Number.isNaN(h) && !Number.isNaN(m)) {
        defaultStart = dispatchDateTime(selectedDate, h, m);
      }
    }

    fetchEmployeeDispatches(selectedEmployee, selectedDate, defaultStart);
  }, [selectedEmployee, selectedDate, employeeList, fetchEmployeeDispatches]);

  // Autosave the current board's complete tasks, dropping any incomplete rows
  // (e.g. the auto-loaded blank task). Reuses saveDispatchItems so the Save
  // button shows its "Saving…" state while autosaving.
  const autosave = useCallback(() => {
    if (!dirtyRef.current) return; // nothing changed since last save
    if (!selectedEmployee || !selectedDate) return;

    const validItems = selectedEmployeeDispatch.filter(
      (item) => validateDispatchItem(item).isValid
    );
    if (validItems.length === 0) return;

    // Snapshot the target board so a tab/date change mid-save can't retarget it.
    const employeeId = selectedEmployee;
    const date = selectedDate;
    dirtyRef.current = false;

    saveDispatchItems(employeeId, date, validItems).then((result) => {
      if (result?.success) {
        setLastSavedAt(new Date());
      } else {
        dirtyRef.current = true; // let the next blur retry
      }
    });
  }, [selectedEmployee, selectedDate, selectedEmployeeDispatch, saveDispatchItems]);

  // Keep a live ref so unmount / tab-hidden flushes use the latest closure.
  useEffect(() => {
    autosaveRef.current = autosave;
  }, [autosave]);

  // Reset the dirty flag + saved indicator whenever the board switches.
  useEffect(() => {
    dirtyRef.current = false;
    setLastSavedAt(null);
  }, [selectedEmployee, selectedDate]);

  // Flush on tab-hide and on unmount (navigating away) so edits aren't lost.
  useEffect(() => {
    const flush = () => autosaveRef.current && autosaveRef.current();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      flush();
    };
  }, []);

  // Autosave when focus leaves the grid entirely. Deferred so activeElement
  // reflects the new focus target; ignores portaled MUI popups (time pickers,
  // select menus, the checklist modal) so mid-edit interactions don't save.
  const handleBoardBlur = useCallback(() => {
    setTimeout(() => {
      const active = document.activeElement;
      const wrap = boardWrapRef.current;
      if (wrap && active && wrap.contains(active)) return;
      if (
        active &&
        active.closest &&
        active.closest(
          ".MuiPopover-root, .MuiPickersPopper-root, .MuiModal-root, .MuiAutocomplete-popper, .MuiMenu-root"
        )
      ) {
        return;
      }
      autosave();
    }, 0);
  }, [autosave]);

  // Weekday (UTC, to match the Firestore date key) for the selected date
  const scheduleDayName = useMemo(
    () =>
      new Date(selectedDate).toLocaleDateString("en-US", {
        weekday: "long",
        timeZone: "UTC",
      }),
    [selectedDate]
  );

  // Employees scheduled to work on the selected day
  const scheduledEmployees = useMemo(
    () =>
      employeeList.filter((emp) => emp.schedule && emp.schedule[scheduleDayName]),
    [employeeList, scheduleDayName]
  );

  // Tabs = scheduled employees first, plus any manually added for this day (deduped)
  const tabEmployees = useMemo(() => {
    const byId = new Map();
    scheduledEmployees.forEach((emp) => byId.set(emp.id, emp));
    addedEmployeeIds.forEach((id) => {
      if (!byId.has(id)) {
        const emp = employeeList.find((e) => e.id === id);
        if (emp) byId.set(id, emp);
      }
    });
    return Array.from(byId.values());
  }, [scheduledEmployees, addedEmployeeIds, employeeList]);

  // Employees that can still be added (not already shown as a tab)
  const addableEmployees = useMemo(
    () => employeeList.filter((emp) => !tabEmployees.some((t) => t.id === emp.id)),
    [employeeList, tabEmployees]
  );

  // Keep a tab selected: default to the first, and recover if the active
  // employee leaves the list (e.g. after changing the date).
  React.useEffect(() => {
    if (tabEmployees.length === 0) {
      if (selectedEmployee !== null) setSelectedEmployee(null);
      return;
    }
    if (!tabEmployees.some((emp) => emp.id === selectedEmployee)) {
      setSelectedEmployee(tabEmployees[0].id);
    }
  }, [tabEmployees, selectedEmployee]);

  // Memoized handlers
  const handleSelectEmployeeTab = useCallback(
    (employeeId) => {
      if (employeeId === selectedEmployee) return;
      autosave(); // persist current board before switching
      setSelectedEmployee(employeeId);
    },
    [autosave, selectedEmployee]
  );

  const handleOpenAddMenu = useCallback((event) => {
    setAddMenuAnchor(event.currentTarget);
  }, []);

  const handleCloseAddMenu = useCallback(() => {
    setAddMenuAnchor(null);
  }, []);

  const handleAddEmployeeTab = useCallback(
    (employeeId) => {
      autosave(); // persist current board before switching
      setAddedEmployeeIds((prev) =>
        prev.includes(employeeId) ? prev : [...prev, employeeId]
      );
      setSelectedEmployee(employeeId);
      setAddMenuAnchor(null);
    },
    [autosave]
  );

  const handleDateChange = useCallback(
    (newDate) => {
      if (newDate && newDate.isValid()) {
        autosave(); // persist current board before changing date
        setSelectedDate(newDate.$d.setUTCHours(6, 0, 0, 0));
        // Manual add-ins are per-day; clear them when the date changes
        setAddedEmployeeIds([]);
      }
    },
    [autosave]
  );

  const handleCellEdit = useCallback(
    (id, field, value) => {
      dirtyRef.current = true;

      // Re-anchor time edits to the selected dispatch date (the TimePicker
      // defaults to today's date) so times sort/calculate correctly for any day.
      let nextValue = value;
      if ((field === "startTime" || field === "endTime") && value) {
        const picked = dayjs(value);
        if (picked.isValid()) {
          nextValue = dispatchDateTime(selectedDate, picked.hour(), picked.minute());
        }
      }

      updateDispatchItem(id, field, nextValue);

      // Auto-add new row when both start and end time are set
      if (field === "endTime" && value) {
        const item = selectedEmployeeDispatch.find((row) => row.id === id);
        if (item?.startTime) {
          addNewDispatchRow();
        }
      }
    },
    [updateDispatchItem, selectedEmployeeDispatch, addNewDispatchRow, selectedDate]
  );

  const handleDeleteRow = useCallback(
    (id) => {
      dirtyRef.current = true;
      removeDispatchItem(id);
    },
    [removeDispatchItem]
  );

  // Checklist modal handlers (must be defined before columns)
  const handleOpenChecklistModal = useCallback((dispatchId, checklists) => {
    setSelectedDispatchId(dispatchId);
    setSelectedChecklists(checklists || []);
    setChecklistModalOpen(true);
  }, []);

  const handleCloseChecklistModal = useCallback(() => {
    setChecklistModalOpen(false);
    setSelectedDispatchId(null);
    setSelectedChecklists([]);
  }, []);

  const handleSaveChecklists = useCallback((updatedChecklists) => {
    if (!selectedDispatchId) return;

    // Update the dispatch item with new checklists
    dirtyRef.current = true;
    updateDispatchItem(selectedDispatchId, "checklists", updatedChecklists);

    setAlert({
      show: true,
      message: "Checklists updated successfully",
      severity: "success",
    });

    // Auto-hide success message
    setTimeout(() => {
      setAlert({ show: false, message: "", severity: "info" });
    }, 3000);
  }, [selectedDispatchId, updateDispatchItem]);

  const handleSave = useCallback(async () => {
    if (!selectedEmployee || !selectedDate) {
      setAlert({
        show: true,
        message: "Please select an employee and date",
        severity: "error",
      });
      return;
    }

    // Save only complete tasks — incomplete rows (e.g. the auto-loaded blank
    // task) are disregarded, matching autosave behavior.
    const validItems = selectedEmployeeDispatch.filter(
      (item) => validateDispatchItem(item).isValid
    );
    if (validItems.length === 0) {
      setAlert({
        show: true,
        message: "No complete tasks to save yet.",
        severity: "warning",
      });
      return;
    }

    const result = await saveDispatchItems(
      selectedEmployee,
      selectedDate,
      validItems
    );

    if (result.success) {
      dirtyRef.current = false;
      setLastSavedAt(new Date());
    }

    setAlert({
      show: true,
      message: result.success
        ? `Saved ${validItems.length} task${validItems.length !== 1 ? "s" : ""}`
        : result.message,
      severity: result.success ? "success" : "error",
    });

    // Auto-hide success message
    if (result.success) {
      setTimeout(() => {
        setAlert({ show: false, message: "", severity: "info" });
      }, 3000);
    }
  }, [
    selectedEmployee,
    selectedDate,
    selectedEmployeeDispatch,
    saveDispatchItems,
  ]);

  // Memoized columns
  const columns = useMemo(
    () =>
      createDispatchColumns(
        handleCellEdit,
        handleDeleteRow,
        employeeList,
        roomsList,
        selectedEmployee,
        handleOpenChecklistModal
      ),
    [handleCellEdit, handleDeleteRow, employeeList, roomsList, selectedEmployee, handleOpenChecklistModal]
  );

  // Close alert handler
  const handleCloseAlert = useCallback(() => {
    setAlert({ show: false, message: "", severity: "info" });
  }, []);

  // Auto-dispatch handlers
  const handleOpenAutoDispatch = useCallback(() => {
    if (!selectedEmployee || !selectedDate) {
      setAlert({
        show: true,
        message: "Please select an employee and date first",
        severity: "warning",
      });
      return;
    }
    setAutoDispatchModalOpen(true);
  }, [selectedEmployee, selectedDate]);

  const handleCloseAutoDispatch = useCallback(() => {
    setAutoDispatchModalOpen(false);
  }, []);

  const handleAutoDispatchGenerated = useCallback((generatedTasks) => {
    // Re-anchor generated times (built on a placeholder date) to the selected day.
    const reanchor = (val) => {
      if (!val) return val;
      const p = dayjs(val);
      return p.isValid()
        ? dispatchDateTime(selectedDate, p.hour(), p.minute())
        : val;
    };

    // Format the generated tasks for the UI
    const formattedTasks = generatedTasks.map(task => ({
      ...createNewDispatchItem(),
      startTime: reanchor(task.startTime),
      endTime: reanchor(task.endTime),
      task: task.task,
      location: task.location,
      notes: task.notes || "",
      status: "pending"
    }));

    // Replace all current dispatch items with the generated ones
    dirtyRef.current = true;
    replaceAllDispatchItems(formattedTasks);

    setAlert({
      show: true,
      message: `Auto-generated ${generatedTasks.length} tasks successfully!`,
      severity: "success",
    });

    // Auto-hide success message
    setTimeout(() => {
      setAlert({ show: false, message: "", severity: "info" });
    }, 3000);
  }, [replaceAllDispatchItems, selectedDate]);

  // Print dispatch handler
  const handlePrintDispatch = useCallback(() => {
    if (!selectedEmployee || !selectedDate || selectedEmployeeDispatch.length === 0) {
      setAlert({
        show: true,
        message: "Please select an employee, date, and ensure there are tasks to print",
        severity: "warning",
      });
      return;
    }

    try {
      const employee = employeeList.find(emp => emp.id === selectedEmployee);
      const result = downloadDispatchPDF({
        employeeName: employee?.name || 'Unknown Employee',
        selectedDate: selectedDate,
        dispatchTasks: selectedEmployeeDispatch
      });

      if (result.success) {
        setAlert({
          show: true,
          message: `Dispatch PDF generated: ${result.fileName}`,
          severity: "success",
        });

        // Auto-hide success message
        setTimeout(() => {
          setAlert({ show: false, message: "", severity: "info" });
        }, 3000);
      } else {
        setAlert({
          show: true,
          message: "Failed to generate PDF",
          severity: "error",
        });
      }
    } catch (error) {
      console.error('Error generating dispatch PDF:', error);
      setAlert({
        show: true,
        message: "Error generating PDF",
        severity: "error",
      });
    }
  }, [selectedEmployee, selectedDate, selectedEmployeeDispatch, employeeList]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Alert */}
      {alert.show && (
        <Alert
          severity={alert.severity}
          onClose={handleCloseAlert}
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Header Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Employee Dispatch Management
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {scheduleDayName} · {tabEmployees.length} employee
          {tabEmployees.length === 1 ? "" : "s"} on the board
        </Typography>

        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Select Date"
              value={selectedDate ? dayjs(selectedDate) : null}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  size: "medium",
                },
              }}
            />
          </LocalizationProvider>

          <Divider orientation="vertical" flexItem />

          <Button
            variant="outlined"
            startIcon={<AutoFixHighIcon />}
            onClick={handleOpenAutoDispatch}
            disabled={!selectedEmployee || !selectedDate}
            sx={{ minWidth: 160 }}
          >
            Auto-Dispatch
          </Button>
        </Box>
      </Paper>

      {/* Dispatch grid for the active employee — autosaves on focus-out */}
      <Box ref={boardWrapRef} onBlur={handleBoardBlur}>
      {selectedEmployee ? (
        <Paper elevation={1} sx={{ mb: 0 }}>
          <DataGrid
            rows={selectedEmployeeDispatch}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
              sorting: {
                sortModel: [{ field: "startTime", sort: "asc" }],
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
              },
            }}
            slots={{
              footer: () => (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderTop: 1,
                    borderColor: 'divider'
                  }}
                >
                  <Tooltip title="Add new task">
                    <IconButton
                      color="primary"
                      onClick={addNewDispatchRow}
                      sx={{
                        border: 1,
                        borderColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.main',
                          color: 'white'
                        }
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>

                  <Typography variant="body2" color="text.secondary">
                    {selectedEmployeeDispatch.length} task{selectedEmployeeDispatch.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              )
            }}
          />
        </Paper>
      ) : (
        <Paper
          elevation={1}
          sx={{ mb: 0, p: 4, textAlign: "center", color: "text.secondary" }}
        >
          <Typography variant="body1">
            No employees scheduled for {scheduleDayName}.
          </Typography>
          <Typography variant="body2">
            Use the &ldquo;+&rdquo; on the tab bar below to dispatch someone anyway.
          </Typography>
        </Paper>
      )}
      </Box>

      {/* Excel-style employee worksheet tabs */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 0.5,
          mb: 2,
          px: 1,
          pt: 0.5,
          borderTop: 2,
          borderColor: "primary.main",
          bgcolor: "action.hover",
          overflowX: "auto",
        }}
      >
        {tabEmployees.map((emp) => {
          const active = emp.id === selectedEmployee;
          return (
            <Box
              key={emp.id}
              onClick={() => handleSelectEmployeeTab(emp.id)}
              sx={{
                cursor: "pointer",
                px: 2,
                py: 1,
                whiteSpace: "nowrap",
                fontSize: "0.875rem",
                fontWeight: active ? 600 : 400,
                color: active ? "primary.main" : "text.secondary",
                bgcolor: active ? "background.paper" : "transparent",
                border: 1,
                borderColor: active ? "divider" : "transparent",
                borderBottom: "none",
                borderTopLeftRadius: 6,
                borderTopRightRadius: 6,
                boxShadow: active ? "0 -2px 4px rgba(0,0,0,0.08)" : "none",
                transform: active ? "translateY(-2px)" : "none",
                "&:hover": {
                  bgcolor: active ? "background.paper" : "action.selected",
                },
              }}
            >
              {emp.name}
            </Box>
          );
        })}

        <Tooltip title="Add another employee for this day">
          <span>
            <IconButton
              size="small"
              onClick={handleOpenAddMenu}
              disabled={addableEmployees.length === 0}
              sx={{ mb: 0.5 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={handleCloseAddMenu}
        >
          {addableEmployees.map((emp) => (
            <MenuItem key={emp.id} onClick={() => handleAddEmployeeTab(emp.id)}>
              {emp.name} ({emp.type})
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Action Buttons */}
      {selectedEmployee && (
        <Box display="flex" gap={2} justifyContent="flex-end" alignItems="center">
          {(saving || lastSavedAt) && (
            <Typography
              variant="body2"
              color={saving ? "text.secondary" : "success.main"}
              sx={{ mr: "auto", display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {saving
                ? "Autosaving…"
                : `✓ Autosaved ${dayjs(lastSavedAt).format("h:mm A")}`}
            </Typography>
          )}
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrintDispatch}
            disabled={selectedEmployeeDispatch.length === 0}
            size="large"
          >
            Print Dispatch
          </Button>

          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || selectedEmployeeDispatch.length === 0}
            size="large"
          >
            {saving ? "Saving..." : "Save Tasks"}
          </Button>
        </Box>
      )}

      {/* Auto-Dispatch Modal */}
      <AutoDispatchModal
        open={autoDispatchModalOpen}
        onClose={handleCloseAutoDispatch}
        selectedEmployee={employeeList.find(emp => emp.id === selectedEmployee)}
        selectedDate={selectedDate}
        onDispatchGenerated={handleAutoDispatchGenerated}
        selectedProperty={selectedProperty}
        employeeList={employeeList}
      />

      {/* Checklist Modal */}
      <ChecklistModal
        open={checklistModalOpen}
        onClose={handleCloseChecklistModal}
        checklists={selectedChecklists}
        onSave={handleSaveChecklists}
        readOnly={false}
        selectedProperty={selectedProperty}
        clientId={selectedClient}
      />
    </Box>
  );
});

DispatchNew.displayName = "DispatchNew";

export default DispatchNew;
